import {
    type EventStreamLine,
    type RunStateRead,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import {
    applyStreamLine,
    applyStreamResponse,
    createTranscriptOrigin,
    type ResponseApplyResult,
    type TranscriptAction,
} from '@apps/web/features/phoebe_chat_v3/model/ingestion';
import {
    type ScopeDimensions,
    type TranscriptState,
} from '@apps/web/features/phoebe_chat_v3/model/state';

const WEAK_EVIDENCE_MAX_AGE_MS = 40_000;
const STRONG_EVIDENCE_MAX_AGE_MS = 600_000;

/**
 * Elapsed time that accrues only while the document is visible.
 *
 * Thinking-state aging and the unresolved-interrupt deadline are promises to a
 * person watching the screen, so they must be measured in the time that person
 * actually spent watching. A tab left hidden overnight would otherwise return
 * with a stale, alarming state that nothing had gone wrong to produce. Wall
 * clock stays the right source for cursor and event timestamps, which describe
 * server facts rather than what someone has waited through.
 */
export type ForegroundClock = Readonly<{
    readonly elapsedMs: () => number;
    readonly setVisible: (visible: boolean) => void;
}>;

export function createForegroundClock(
    monotonicNowMs: () => number,
    initiallyVisible: boolean,
): ForegroundClock {
    let accumulatedMs = 0;
    let visibleSinceMs = initiallyVisible ? monotonicNowMs() : null;

    return {
        elapsedMs(): number {
            if (visibleSinceMs === null) {
                return accumulatedMs;
            }
            return accumulatedMs + (monotonicNowMs() - visibleSinceMs);
        },
        setVisible(visible: boolean): void {
            if (visible) {
                // Repeated visible transitions must not restart the segment, or
                // a burst of focus events would discard accrued time.
                if (visibleSinceMs === null) {
                    visibleSinceMs = monotonicNowMs();
                }
                return;
            }
            if (visibleSinceMs !== null) {
                accumulatedMs += monotonicNowMs() - visibleSinceMs;
                visibleSinceMs = null;
            }
        },
    };
}

export type WaitingForInput = 'approval' | 'user_input' | 'both';

/**
 * The one typed reading of the run-state pending-input flags: which action
 * the run is blocked on from the user, or null when it is not blocked. Both
 * waiting evidence and the response phase's working-but-held variant derive
 * from this, so no surface interprets the raw flags on its own.
 */
export function runStateWaitingFor(
    runState: RunStateRead | null,
): WaitingForInput | null {
    if (runState === null) {
        return null;
    }
    if (runState.has_pending_approval) {
        return runState.has_pending_user_input ? 'both' : 'approval';
    }
    return runState.has_pending_user_input ? 'user_input' : null;
}

export type ThinkingEvidence =
    | Readonly<{ readonly kind: 'idle' | 'terminal' | 'interrupted' }>
    | Readonly<{
          readonly kind: 'waiting';
          readonly waitingFor: WaitingForInput;
      }>
    | Readonly<{
          readonly kind: 'thinking';
          readonly ageMs: number;
          readonly source: 'strong' | 'handoff' | 'running';
      }>
    | Readonly<{
          readonly kind: 'queued';
          readonly ageMs: number;
          readonly source: 'pending_work';
      }>
    | Readonly<{
          readonly kind: 'background_work';
          readonly ageMs: number;
          readonly source: 'strong';
      }>
    | Readonly<{
          readonly kind: 'stale';
          readonly ageMs: number;
          readonly source: 'handoff' | 'pending_work' | 'running';
      }>;

export function thinkingEvidenceTransitionInMs(
    evidence: ThinkingEvidence,
): number | null {
    if (evidence.kind === 'queued') {
        return Math.max(0, WEAK_EVIDENCE_MAX_AGE_MS - evidence.ageMs);
    }
    if (evidence.kind !== 'thinking') {
        return null;
    }
    const boundaryMs =
        evidence.source === 'strong'
            ? STRONG_EVIDENCE_MAX_AGE_MS
            : WEAK_EVIDENCE_MAX_AGE_MS;
    return Math.max(0, boundaryMs - evidence.ageMs);
}

type EvidenceOrigins = Readonly<{
    readonly pendingWorkObservedAtMs: number | null;
    readonly runningObservedAtMs: number | null;
    readonly strongActivityBeganAtMs: number | null;
    readonly strongHandoffBeganAtMs: number | null;
}>;

export type TranscriptStore = Readonly<{
    readonly foregroundClock: ForegroundClock;
    readonly getSnapshot: () => TranscriptState;
    readonly getThinkingEvidence: () => ThinkingEvidence;
    readonly subscribe: (listener: () => void) => () => void;
    readonly ingestResponse: (
        lines: ReadonlyArray<EventStreamLine>,
        nowMs: number,
    ) => ResponseApplyResult;
    readonly dispatchLocal: (action: TranscriptAction) => void;
}>;

export function createTranscriptStore(
    scope: ScopeDimensions,
    nowMs: number,
    injectedForegroundClock: ForegroundClock,
): TranscriptStore {
    let state = createTranscriptOrigin(scope, nowMs);
    let evidenceOrigins: EvidenceOrigins = {
        pendingWorkObservedAtMs: null,
        runningObservedAtMs: null,
        strongActivityBeganAtMs: null,
        strongHandoffBeganAtMs: null,
    };
    const listeners = new Set<() => void>();

    function notifyListeners(): void {
        const listenerSnapshot = Array.from(listeners);
        for (const listener of listenerSnapshot) {
            listener();
        }
    }

    function syncEvidenceOrigins(nextState: TranscriptState): void {
        const foregroundNowMs = injectedForegroundClock.elapsedMs();

        // Lifecycle stores wall-clock receipt facts. Thinking promises visible
        // waiting time, so these private origins use the foreground clock but
        // consume ingestion's lifecycle transitions instead of repeating its
        // run-state transition ladder.
        function syncOrigin(
            previousOriginMs: number | null,
            nextOriginMs: number | null,
            foregroundOriginMs: number | null,
        ): number | null {
            if (nextOriginMs === null) {
                return null;
            }
            if (nextOriginMs !== previousOriginMs) {
                return foregroundNowMs;
            }
            if (foregroundOriginMs === null) {
                throw new Error(
                    'Lifecycle evidence is missing its foreground origin.',
                );
            }
            return foregroundOriginMs;
        }

        evidenceOrigins = {
            pendingWorkObservedAtMs: syncOrigin(
                state.lifecycle.pendingWorkObservedAtMs,
                nextState.lifecycle.pendingWorkObservedAtMs,
                evidenceOrigins.pendingWorkObservedAtMs,
            ),
            runningObservedAtMs: syncOrigin(
                state.lifecycle.runningObservedAtMs,
                nextState.lifecycle.runningObservedAtMs,
                evidenceOrigins.runningObservedAtMs,
            ),
            strongActivityBeganAtMs: syncOrigin(
                state.lifecycle.strongActivityBeganAtMs,
                nextState.lifecycle.strongActivityBeganAtMs,
                evidenceOrigins.strongActivityBeganAtMs,
            ),
            strongHandoffBeganAtMs: syncOrigin(
                state.lifecycle.strongHandoffBeganAtMs,
                nextState.lifecycle.strongHandoffBeganAtMs,
                evidenceOrigins.strongHandoffBeganAtMs,
            ),
        };
    }

    function getThinkingEvidence(): ThinkingEvidence {
        const foregroundElapsedMs = injectedForegroundClock.elapsedMs();
        const runState = state.runState;
        if (runState === null) {
            return { kind: 'idle' };
        }
        const strongNow = runState.is_generating || runState.has_active_lease;
        const waitingFor = runStateWaitingFor(runState);
        if (!strongNow && waitingFor !== null) {
            return {
                kind: 'waiting',
                waitingFor,
            };
        }
        if (strongNow) {
            if (evidenceOrigins.strongActivityBeganAtMs === null) {
                throw new Error('Strong evidence is missing its origin.');
            }
            const ageMs = Math.max(
                0,
                foregroundElapsedMs - evidenceOrigins.strongActivityBeganAtMs,
            );
            return ageMs >= STRONG_EVIDENCE_MAX_AGE_MS
                ? {
                      kind: 'background_work',
                      ageMs,
                      source: 'strong',
                  }
                : {
                      kind: 'thinking',
                      ageMs,
                      source: 'strong',
                  };
        }
        if (evidenceOrigins.strongHandoffBeganAtMs !== null) {
            const ageMs = Math.max(
                0,
                foregroundElapsedMs - evidenceOrigins.strongHandoffBeganAtMs,
            );
            return ageMs >= WEAK_EVIDENCE_MAX_AGE_MS
                ? {
                      kind: 'stale',
                      ageMs,
                      source: 'handoff',
                  }
                : {
                      kind: 'thinking',
                      ageMs,
                      source: 'handoff',
                  };
        }
        if (runState.has_pending_work) {
            if (evidenceOrigins.pendingWorkObservedAtMs === null) {
                throw new Error('Pending-work evidence is missing its origin.');
            }
            const ageMs = Math.max(
                0,
                foregroundElapsedMs - evidenceOrigins.pendingWorkObservedAtMs,
            );
            return ageMs >= WEAK_EVIDENCE_MAX_AGE_MS
                ? {
                      kind: 'stale',
                      ageMs,
                      source: 'pending_work',
                  }
                : {
                      kind: 'queued',
                      ageMs,
                      source: 'pending_work',
                  };
        }
        if (runState.interrupt_requested) {
            return { kind: 'interrupted' };
        }
        if (runState.status === 'running') {
            if (evidenceOrigins.runningObservedAtMs === null) {
                throw new Error('Running evidence is missing its origin.');
            }
            const ageMs = Math.max(
                0,
                foregroundElapsedMs - evidenceOrigins.runningObservedAtMs,
            );
            return ageMs >= WEAK_EVIDENCE_MAX_AGE_MS
                ? {
                      kind: 'stale',
                      ageMs,
                      source: 'running',
                  }
                : {
                      kind: 'thinking',
                      ageMs,
                      source: 'running',
                  };
        }
        if (
            runState.status === 'completed' ||
            runState.status === 'failed' ||
            runState.status === 'cancelled'
        ) {
            return { kind: 'terminal' };
        }
        return { kind: 'idle' };
    }

    return {
        foregroundClock: injectedForegroundClock,
        getSnapshot(): TranscriptState {
            return state;
        },
        getThinkingEvidence,
        subscribe(listener: () => void): () => void {
            listeners.add(listener);
            let subscribed = true;
            return function unsubscribe(): void {
                if (!subscribed) {
                    return;
                }
                subscribed = false;
                listeners.delete(listener);
            };
        },
        ingestResponse(
            lines: ReadonlyArray<EventStreamLine>,
            responseNowMs: number,
        ): ResponseApplyResult {
            const result = applyStreamResponse(state, lines, responseNowMs);
            syncEvidenceOrigins(result.state);
            state = result.state;
            notifyListeners();
            return result;
        },
        dispatchLocal(action: TranscriptAction): void {
            const result = applyStreamLine(state, action);
            if (action.type === 'scope_reset') {
                evidenceOrigins = {
                    pendingWorkObservedAtMs: null,
                    runningObservedAtMs: null,
                    strongActivityBeganAtMs: null,
                    strongHandoffBeganAtMs: null,
                };
            }
            state = result.state;
            notifyListeners();
        },
    };
}
